import { useState } from 'react';
import { MapPin, Loader, CheckCircle, AlertTriangle, Copy, Check, Camera } from 'lucide-react';
import { logEvent } from '../lib/telemetry';
import { resolvePhotoLocationDetailed, type PhotoGeo, type PhotoGeoDiagnostic } from '../lib/photoLocation';
import { MiniMapPreview } from './MiniMapPreview';
import { LocationPicker } from './LocationPicker';
import type { GPSLocation } from '../lib/googleMaps';

/**
 * Outcome labels for the photo-location self-test. Plain English rather than
 * the raw enum, because the person running this on an iPad in a car park is
 * trying to answer "will my photos get a pin, and if not why".
 */
const TIER_OUTCOME_LABEL: Record<string, string> = {
  hit: 'Found a location',
  miss: 'Ran, found nothing',
  skipped_not_jpeg: 'Skipped — not a JPEG',
  skipped_no_geolocation: 'Skipped — no geolocation in this browser',
  skipped_not_granted: 'Skipped — location not granted this session',
  miss_no_fix: 'No fix recorded yet this session',
  miss_stale: 'Last fix is too old to trust',
  miss_timeout: 'Timed out waiting for the device',
};

type ModeStatus = 'idle' | 'running' | 'ok' | 'error';

interface ModeResult {
  status: ModeStatus;
  latitude?: number;
  longitude?: number;
  accuracy?: number | null;
  elapsedMs?: number;
  errorCode?: number;
  errorName?: string;
  errorMessage?: string;
}

interface Sample {
  elapsedMs: number;
  accuracy: number | null;
  latitude: number;
  longitude: number;
}

interface AwareResult {
  status: ModeStatus;
  best?: Sample | null;
  samples?: Sample[];
  elapsedMs?: number;
  converged?: boolean;
  errorName?: string;
  errorMessage?: string;
}

// Accuracy-aware capture target: keep watching until the fix is at least this
// good (then stop early), or until MAX_WAIT elapses (then use the best so far).
const TARGET_ACCURACY_M = 25;
const MAX_WAIT_MS = 20000;

const HIGH_OPTS: PositionOptions = { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 };
// maximumAge: 0 so the low-accuracy run re-acquires instead of handing back the
// high-accuracy fix from a few seconds earlier — otherwise the two cards would
// always look identical. (Production fallback keeps a 30s cache for speed; this
// is a diagnostic that needs the two modes measured independently.)
const LOW_OPTS: PositionOptions = { enableHighAccuracy: false, timeout: 12000, maximumAge: 0 };

function errName(code: number): string {
  return code === 1 ? 'permission_denied' : code === 2 ? 'position_unavailable' : code === 3 ? 'timeout' : `unknown_${code}`;
}

function captureOnce(opts: PositionOptions): Promise<ModeResult> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({ status: 'error', errorCode: 2, errorName: 'position_unavailable', errorMessage: 'Geolocation not available' });
      return;
    }
    const t0 = performance.now();
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          status: 'ok',
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy != null ? Math.round(pos.coords.accuracy) : null,
          elapsedMs: Math.round(performance.now() - t0),
        });
      },
      (err) => {
        resolve({
          status: 'error',
          elapsedMs: Math.round(performance.now() - t0),
          errorCode: err.code,
          errorName: errName(err.code),
          errorMessage: err.message,
        });
      },
      opts,
    );
  });
}

/**
 * Accuracy-aware capture (the proposed app behaviour). Instead of taking the
 * first single-shot reading — which during GPS warm-up is the coarse ~hundreds-
 * of-metres network fix — this watches the position stream and keeps the most
 * accurate reading, stopping as soon as it reaches TARGET_ACCURACY_M or when
 * MAX_WAIT_MS elapses (using the best seen so far). `onSample` streams each
 * reading so the UI can show the convergence live.
 */
function accuracyAwareCapture(onSample: (s: Sample) => void): Promise<AwareResult> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({ status: 'error', errorName: 'position_unavailable', errorMessage: 'Geolocation not available' });
      return;
    }
    const t0 = performance.now();
    const samples: Sample[] = [];
    let best: Sample | null = null;
    let done = false;
    let watchId = -1;

    const finish = (converged: boolean) => {
      if (done) return;
      done = true;
      if (watchId !== -1) navigator.geolocation.clearWatch(watchId);
      clearTimeout(timer);
      resolve({
        status: best ? 'ok' : 'error',
        best,
        samples,
        elapsedMs: Math.round(performance.now() - t0),
        converged,
        errorName: best ? undefined : 'timeout',
        errorMessage: best ? undefined : 'No position acquired before timeout',
      });
    };

    const timer = setTimeout(() => finish(false), MAX_WAIT_MS);

    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const s: Sample = {
          elapsedMs: Math.round(performance.now() - t0),
          accuracy: pos.coords.accuracy != null ? Math.round(pos.coords.accuracy) : null,
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        };
        samples.push(s);
        if (best === null || (s.accuracy != null && (best.accuracy == null || s.accuracy < best.accuracy))) {
          best = s;
        }
        onSample(s);
        if (s.accuracy != null && s.accuracy <= TARGET_ACCURACY_M) finish(true);
      },
      (err) => {
        // Only fail outright if nothing has come in yet; otherwise let the
        // timeout settle with the best reading we already have.
        if (samples.length === 0 && !done) {
          done = true;
          if (watchId !== -1) navigator.geolocation.clearWatch(watchId);
          clearTimeout(timer);
          resolve({
            status: 'error',
            samples,
            elapsedMs: Math.round(performance.now() - t0),
            converged: false,
            errorName: errName(err.code),
            errorMessage: err.message,
          });
        }
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: MAX_WAIT_MS },
    );
  });
}

function accuracyColor(m: number | null): string {
  if (m == null) return 'text-theme-secondary';
  if (m <= TARGET_ACCURACY_M) return 'text-emerald-700 dark:text-emerald-300';
  if (m <= 100) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function haversineMeters(a: GPSLocation, b: GPSLocation): number {
  const R = 6371000;
  const dLat = ((b.latitude - a.latitude) * Math.PI) / 180;
  const dLng = ((b.longitude - a.longitude) * Math.PI) / 180;
  const la1 = (a.latitude * Math.PI) / 180;
  const la2 = (b.latitude * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function ModeCard({ title, subtitle, result }: { title: string; subtitle: string; result: ModeResult }) {
  return (
    <div className="rounded-xl border border-line bg-surface p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="font-semibold text-theme-primary">{title}</h3>
          <p className="text-xs text-theme-secondary">{subtitle}</p>
        </div>
        {result.status === 'running' && <Loader size={18} className="animate-spin text-blue-500" />}
        {result.status === 'ok' && <CheckCircle size={18} className="text-emerald-500" />}
        {result.status === 'error' && <AlertTriangle size={18} className="text-red-500" />}
      </div>
      {result.status === 'idle' && <p className="text-sm text-theme-secondary">Not run yet</p>}
      {result.status === 'running' && <p className="text-sm text-blue-600 dark:text-blue-400">Acquiring…</p>}
      {result.status === 'ok' && (
        <>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <dt className="text-theme-secondary">Accuracy</dt>
            <dd className="font-medium text-theme-primary">{result.accuracy != null ? `${result.accuracy} m` : 'n/a'}</dd>
            <dt className="text-theme-secondary">Time to fix</dt>
            <dd className="font-medium text-theme-primary">{result.elapsedMs} ms</dd>
            <dt className="text-theme-secondary">Latitude</dt>
            <dd className="font-mono text-theme-primary">{result.latitude?.toFixed(6)}</dd>
            <dt className="text-theme-secondary">Longitude</dt>
            <dd className="font-mono text-theme-primary">{result.longitude?.toFixed(6)}</dd>
          </dl>
          {result.latitude != null && result.longitude != null && (
            <div className="mt-3">
              <MiniMapPreview latitude={result.latitude} longitude={result.longitude} height={120} zoom={18} />
            </div>
          )}
        </>
      )}
      {result.status === 'error' && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <dt className="text-theme-secondary">Error</dt>
          <dd className="font-medium text-red-700 dark:text-red-300">{result.errorName}</dd>
          <dt className="text-theme-secondary">After</dt>
          <dd className="font-medium text-theme-primary">{result.elapsedMs} ms</dd>
          <dt className="text-theme-secondary">Message</dt>
          <dd className="col-span-1 text-theme-primary">{result.errorMessage || '—'}</dd>
        </dl>
      )}
    </div>
  );
}

export function GpsDiagnostics() {
  const [high, setHigh] = useState<ModeResult>({ status: 'idle' });
  const [low, setLow] = useState<ModeResult>({ status: 'idle' });
  const [permission, setPermission] = useState<string>('unknown');
  const [running, setRunning] = useState(false);
  const [copied, setCopied] = useState(false);
  const [aware, setAware] = useState<AwareResult>({ status: 'idle' });
  const [liveSamples, setLiveSamples] = useState<Sample[]>([]);
  const [awareRunning, setAwareRunning] = useState(false);
  const [pickerSeed, setPickerSeed] = useState<GPSLocation | null>(null);
  const [pickerSeedAccuracy, setPickerSeedAccuracy] = useState<number | null>(null);
  const [picked, setPicked] = useState<GPSLocation | null>(null);
  const [pickerSaved, setPickerSaved] = useState<GPSLocation | null>(null);
  const [photoDiag, setPhotoDiag] = useState<{ geo: PhotoGeo | null; diag: PhotoGeoDiagnostic } | null>(null);
  const [photoRunning, setPhotoRunning] = useState(false);
  const [photoSource, setPhotoSource] = useState<'synthetic' | 'file'>('synthetic');
  const [pickerBusy, setPickerBusy] = useState(false);

  const geoAvailable = typeof navigator !== 'undefined' && !!navigator.geolocation;
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';

  const runTest = async () => {
    setRunning(true);
    setHigh({ status: 'running' });
    setLow({ status: 'idle' });
    setCopied(false);

    let perm = 'unknown';
    try {
      const permApi = (navigator as Navigator & { permissions?: Permissions }).permissions;
      if (permApi?.query) {
        const status = await permApi.query({ name: 'geolocation' as PermissionName });
        perm = status.state;
      }
    } catch {
      perm = 'unsupported';
    }
    setPermission(perm);

    void logEvent('gps_diagnostic_started', 'Manual GPS diagnostic test started', {
      metadata: { permission: perm, geo_available: geoAvailable },
    });

    const highResult = await captureOnce(HIGH_OPTS);
    setHigh(highResult);

    setLow({ status: 'running' });
    const lowResult = await captureOnce(LOW_OPTS);
    setLow(lowResult);

    void logEvent('gps_diagnostic_completed', 'Manual GPS diagnostic test completed', {
      metadata: {
        permission: perm,
        high: { status: highResult.status, accuracy_m: highResult.accuracy ?? null, elapsed_ms: highResult.elapsedMs ?? null, error: highResult.errorName ?? null },
        low: { status: lowResult.status, accuracy_m: lowResult.accuracy ?? null, elapsed_ms: lowResult.elapsedMs ?? null, error: lowResult.errorName ?? null },
      },
    });

    setRunning(false);
  };

  const runAwareTest = async () => {
    setAwareRunning(true);
    setAware({ status: 'running' });
    setLiveSamples([]);
    void logEvent('gps_diagnostic_aware_started', 'Accuracy-aware GPS capture test started', {
      metadata: { target_m: TARGET_ACCURACY_M, max_wait_ms: MAX_WAIT_MS },
    });
    const result = await accuracyAwareCapture((s) => setLiveSamples((prev) => [...prev, s]));
    setAware(result);
    void logEvent('gps_diagnostic_aware_completed', 'Accuracy-aware GPS capture test completed', {
      metadata: {
        converged: result.converged ?? false,
        best_m: result.best?.accuracy ?? null,
        elapsed_ms: result.elapsedMs ?? null,
        samples: result.samples?.length ?? 0,
        target_m: TARGET_ACCURACY_M,
      },
    });
    setAwareRunning(false);
  };

  const startPinPlacement = async () => {
    setPickerBusy(true);
    setPickerSaved(null);
    const result = await accuracyAwareCapture(() => {});
    if (result.best) {
      const seed: GPSLocation = { latitude: result.best.latitude, longitude: result.best.longitude };
      setPickerSeed(seed);
      setPicked(seed);
      setPickerSeedAccuracy(result.best.accuracy ?? null);
      void logEvent('gps_picker_seeded', 'Location picker seeded from GPS', {
        metadata: { accuracy_m: result.best.accuracy ?? null, elapsed_ms: result.elapsedMs ?? null, converged: result.converged ?? false },
      });
    } else {
      void logEvent('gps_picker_seed_failed', 'Location picker could not seed from GPS', {
        severity: 'warning',
        metadata: { error: result.errorName ?? null },
      });
    }
    setPickerBusy(false);
  };

  /**
   * Run the real photo-location resolver and show what each tier did.
   *
   * The synthetic file is deliberately not a JPEG: EXIF is skipped, so this
   * exercises exactly the two tiers that have to work for an in-browser camera
   * capture (live read, then the tracker's last fix). That is the case that has
   * never produced a coordinate in production, and it can be tested here in a
   * couple of seconds without starting an inspection or writing a report.
   */
  const runPhotoLocationTest = async (file?: File) => {
    setPhotoRunning(true);
    setPhotoSource(file ? 'file' : 'synthetic');
    try {
      const subject = file ?? new File([new Uint8Array([0])], 'diagnostic-probe.bin', { type: 'application/octet-stream' });
      const result = await resolvePhotoLocationDetailed(subject);
      setPhotoDiag(result);
      void logEvent('gps_diag_photo_location', `Photo location self-test: ${result.diag.src}`, {
        metadata: { source: file ? 'file' : 'synthetic', ...result.diag },
      });
    } finally {
      setPhotoRunning(false);
    }
  };

  const savePin = () => {
    if (!picked || !pickerSeed) return;
    setPickerSaved(picked);
    void logEvent('gps_picker_saved', 'Location picker saved (prototype)', {
      metadata: {
        seed_lat: pickerSeed.latitude,
        seed_lng: pickerSeed.longitude,
        final_lat: picked.latitude,
        final_lng: picked.longitude,
        drag_distance_m: Math.round(haversineMeters(pickerSeed, picked)),
        seed_accuracy_m: pickerSeedAccuracy ?? null,
      },
    });
  };

  const copyResults = async () => {
    const payload = JSON.stringify({ when: new Date().toISOString(), userAgent, geoAvailable, permission, high, low, accuracyAware: { converged: aware.converged, best: aware.best, samples: aware.samples?.length, elapsedMs: aware.elapsedMs } }, null, 2);
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard may be blocked; ignore
    }
  };

  const hasResults = high.status === 'ok' || high.status === 'error' || low.status === 'ok' || low.status === 'error';

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-2">
        <MapPin className="text-blue-600" size={28} />
        <h1 className="text-3xl font-bold text-theme-primary">GPS Diagnostics</h1>
      </div>
      <p className="text-theme-secondary mb-6">
        Runs the same two location modes the app uses when you tap “Set GPS”. High accuracy asks the
        real GPS chip; if that times out or fails, the app falls back to the faster low-accuracy
        network fix. Run this on the device that is having trouble to see which mode works and how
        accurate it is. Every run is also saved to the Activity Log.
      </p>
      <p className="text-theme-secondary text-sm mb-6 -mt-3">
        Note: on a WiFi-only iPad (no built-in GPS chip), both modes use network positioning, so they
        will legitimately show the same coordinates and a coarse accuracy (tens of metres). A true GPS
        device shows a tighter accuracy (single-digit metres) on the high-accuracy card.
      </p>

      <div className="rounded-xl border border-line bg-subtle p-4 mb-4 text-sm">
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          <span className="text-theme-secondary">Geolocation available</span>
          <span className={geoAvailable ? 'text-emerald-700 dark:text-emerald-300 font-medium' : 'text-red-700 dark:text-red-300 font-medium'}>
            {geoAvailable ? 'Yes' : 'No'}
          </span>
          <span className="text-theme-secondary">Permission</span>
          <span className="font-medium text-theme-primary">{permission}</span>
          <span className="text-theme-secondary">Device</span>
          <span className="text-theme-primary break-all">{userAgent}</span>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={runTest}
          disabled={running || awareRunning || !geoAvailable}
          className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {running ? <Loader size={18} className="animate-spin" /> : <MapPin size={18} />}
          {running ? 'Running…' : 'Run GPS Test'}
        </button>
        {hasResults && !running && (
          <button
            onClick={copyResults}
            className="inline-flex items-center gap-2 px-4 py-3 border border-line rounded-lg text-theme-primary hover:bg-subtle transition-colors font-medium"
          >
            {copied ? <Check size={18} className="text-emerald-600" /> : <Copy size={18} />}
            {copied ? 'Copied' : 'Copy results'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ModeCard title="High accuracy (GPS)" subtitle="enableHighAccuracy, 15s timeout" result={high} />
        <ModeCard title="Low accuracy (network)" subtitle="fallback, 12s timeout" result={low} />
      </div>

      <div className="mt-8 rounded-xl border-2 border-line bg-subtle p-5">
        <h2 className="text-lg font-bold text-theme-primary mb-1">Accuracy-aware capture (proposed app fix)</h2>
        <p className="text-sm text-theme-secondary mb-4">
          Instead of saving the first reading (which during GPS warm-up is the coarse network fix),
          this watches the position and keeps the most accurate one, stopping as soon as it reaches{' '}
          <span className="font-semibold">≤ {TARGET_ACCURACY_M} m</span> or after {MAX_WAIT_MS / 1000}s.
          Watch it converge below — the first readings are usually hundreds of metres, then it tightens.
        </p>

        <button
          onClick={runAwareTest}
          disabled={running || awareRunning || !geoAvailable}
          className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed mb-4"
        >
          {awareRunning ? <Loader size={18} className="animate-spin" /> : <MapPin size={18} />}
          {awareRunning ? 'Converging…' : 'Run accuracy-aware capture'}
        </button>

        {aware.status === 'ok' && aware.best && (
          <div className={`rounded-lg p-3 mb-3 text-sm font-medium ${aware.converged ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300'}`}>
            {aware.converged
              ? `Locked to ${aware.best.accuracy} m in ${aware.elapsedMs} ms (${aware.samples?.length} readings). Final: ${aware.best.latitude.toFixed(6)}, ${aware.best.longitude.toFixed(6)}`
              : `Best ${aware.best.accuracy} m after ${aware.elapsedMs} ms (${aware.samples?.length} readings) — did not reach ${TARGET_ACCURACY_M} m. Final: ${aware.best.latitude.toFixed(6)}, ${aware.best.longitude.toFixed(6)}`}
          </div>
        )}
        {aware.status === 'ok' && aware.best && (
          <div className="mb-3">
            <p className="text-xs font-medium text-theme-secondary mb-1">Pin at the captured location — verify it sits on the property:</p>
            <MiniMapPreview latitude={aware.best.latitude} longitude={aware.best.longitude} height={220} zoom={18} />
          </div>
        )}
        {aware.status === 'error' && (
          <div className="rounded-lg p-3 mb-3 text-sm font-medium bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300">
            Failed: {aware.errorName} {aware.errorMessage ? `— ${aware.errorMessage}` : ''}
          </div>
        )}

        {(awareRunning || (aware.samples?.length ?? 0) > 0) && (
          <div className="rounded-lg border border-line bg-surface">
            <div className="px-3 py-2 border-b border-line text-xs font-semibold text-theme-secondary grid grid-cols-3">
              <span>Reading</span>
              <span>Time</span>
              <span className="text-right">Accuracy</span>
            </div>
            <ul className="max-h-56 overflow-auto divide-y divide-line">
              {(aware.status === 'running' ? liveSamples : aware.samples ?? []).map((s, i) => {
                const isBest = aware.best != null && s.elapsedMs === aware.best.elapsedMs && s.accuracy === aware.best.accuracy;
                return (
                  <li key={i} className={`px-3 py-1.5 text-sm grid grid-cols-3 items-center ${isBest ? 'bg-emerald-50 dark:bg-emerald-500/10' : ''}`}>
                    <span className="text-theme-secondary">#{i + 1}{isBest ? ' ★' : ''}</span>
                    <span className="text-theme-primary">{(s.elapsedMs / 1000).toFixed(1)}s</span>
                    <span className={`text-right font-medium ${accuracyColor(s.accuracy)}`}>
                      {s.accuracy != null ? `${s.accuracy} m` : 'n/a'}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      <div className="mt-8 rounded-xl border-2 border-line bg-subtle p-5">
        <h2 className="text-lg font-bold text-theme-primary mb-1">Photo location (why photos do or don't get a pin)</h2>
        <p className="text-theme-secondary text-sm mb-4">
          Runs the same resolver a real photo capture uses, and reports what each of the three tiers
          did. "Check without a photo" skips EXIF on purpose, so it tests exactly the two tiers an
          in-browser camera capture depends on. Run it on the device that is having trouble.
        </p>

        <div className="flex flex-wrap gap-3 mb-4">
          <button
            onClick={() => void runPhotoLocationTest()}
            disabled={photoRunning}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-60"
          >
            {photoRunning ? <Loader size={18} className="animate-spin" /> : <MapPin size={18} />}
            Check without a photo
          </button>
          <label className="inline-flex items-center gap-2 px-5 py-2.5 border border-line rounded-lg cursor-pointer text-theme-primary hover:bg-subtle transition-colors font-medium">
            <Camera size={18} />
            Test with a photo…
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void runPhotoLocationTest(f);
                e.target.value = '';
              }}
            />
          </label>
        </div>

        {photoDiag && (
          <div className="rounded-lg border border-line bg-surface p-4 text-sm">
            <div className="mb-3">
              {photoDiag.geo ? (
                <span className="inline-flex items-center gap-2 font-medium text-emerald-700 dark:text-emerald-300">
                  <CheckCircle size={16} />
                  A photo taken now would get a pin (from {photoDiag.diag.src})
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 font-medium text-red-700 dark:text-red-300">
                  <AlertTriangle size={16} />
                  A photo taken now would get NO pin
                </span>
              )}
            </div>

            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
              <span className="text-theme-secondary">Tested with</span>
              <span className="text-theme-primary">
                {photoSource === 'file' ? 'a chosen photo' : 'no photo (tiers 2 and 3 only)'}
              </span>

              <span className="text-theme-secondary">Location granted this session</span>
              <span className={photoDiag.diag.granted ? 'text-emerald-700 dark:text-emerald-300 font-medium' : 'text-red-700 dark:text-red-300 font-medium'}>
                {photoDiag.diag.granted ? 'Yes' : 'No'}
              </span>

              <span className="text-theme-secondary">Tracker's last fix</span>
              <span className="text-theme-primary">
                {photoDiag.diag.fix_age_ms == null
                  ? 'never recorded this session'
                  : `${Math.round(photoDiag.diag.fix_age_ms / 1000)}s ago`}
              </span>

              <span className="text-theme-secondary">1. Photo EXIF</span>
              <span className="text-theme-primary">{TIER_OUTCOME_LABEL[photoDiag.diag.exif] ?? photoDiag.diag.exif}</span>

              <span className="text-theme-secondary">2. Live position</span>
              <span className="text-theme-primary">{TIER_OUTCOME_LABEL[photoDiag.diag.live] ?? photoDiag.diag.live}</span>

              <span className="text-theme-secondary">3. Tracker's last fix</span>
              <span className="text-theme-primary">{TIER_OUTCOME_LABEL[photoDiag.diag.heartbeat] ?? photoDiag.diag.heartbeat}</span>

              {photoDiag.geo && (
                <>
                  <span className="text-theme-secondary">Coordinate</span>
                  <span className="text-theme-primary font-mono">
                    {photoDiag.geo.lat.toFixed(6)}, {photoDiag.geo.lng.toFixed(6)}
                    {photoDiag.geo.acc != null && ` (±${photoDiag.geo.acc} m)`}
                  </span>
                </>
              )}

              <span className="text-theme-secondary">Took</span>
              <span className="text-theme-primary">{photoDiag.diag.elapsed_ms} ms</span>
            </div>

            {!photoDiag.geo && !photoDiag.diag.granted && (
              <p className="mt-3 text-theme-secondary">
                Location has not been granted in this session, so the app will not read a position —
                by design, because a denied prompt would break Set GPS for the whole site. Grant it
                from the tracking badge on an inspection, then run this again.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="mt-8 rounded-xl border-2 border-line bg-subtle p-5">
        <h2 className="text-lg font-bold text-theme-primary mb-1">Place the pin (proposed capture UX)</h2>
        <p className="text-sm text-theme-secondary mb-4">
          Seed a pin from GPS, then drag the map so the centre pin sits on the exact spot and tap Save.
          Whatever you confirm is what gets saved — GPS is only the starting guess, so a poor fix can
          never save the wrong place. The blue circle shows the GPS accuracy around the seed.
        </p>

        <button
          onClick={startPinPlacement}
          disabled={pickerBusy || running || awareRunning || !geoAvailable}
          className="inline-flex items-center gap-2 px-5 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {pickerBusy ? <Loader size={18} className="animate-spin" /> : <MapPin size={18} />}
          {pickerBusy ? 'Getting GPS…' : pickerSeed ? 'Re-seed from GPS' : 'Place pin using GPS'}
        </button>

        {pickerSeed && picked && (
          <div className="mt-4">
            <LocationPicker initial={pickerSeed} accuracyMeters={pickerSeedAccuracy} onChange={setPicked} height="340px" />
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
              <div>
                <div className="text-theme-secondary text-xs">GPS seed{pickerSeedAccuracy != null ? ` (±${pickerSeedAccuracy} m)` : ''}</div>
                <div className="font-mono text-theme-primary">{pickerSeed.latitude.toFixed(6)}, {pickerSeed.longitude.toFixed(6)}</div>
              </div>
              <div>
                <div className="text-theme-secondary text-xs">Pin now</div>
                <div className="font-mono text-theme-primary">{picked.latitude.toFixed(6)}, {picked.longitude.toFixed(6)}</div>
              </div>
              <div>
                <div className="text-theme-secondary text-xs">Moved from GPS</div>
                <div className="font-medium text-theme-primary">{Math.round(haversineMeters(pickerSeed, picked))} m</div>
              </div>
            </div>
            <button
              onClick={savePin}
              className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <Check size={18} /> Save location
            </button>
            {pickerSaved && (
              <div className="mt-3 rounded-lg bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300 p-3 text-sm font-medium">
                Saved {pickerSaved.latitude.toFixed(6)}, {pickerSaved.longitude.toFixed(6)} — this confirmed coordinate is what the app would store.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
